import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import Reveal from "../components/Reveal";
import { GLOSSARY } from "../data/glossary";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const norm = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

export default function Glossaire() {
  const [query, setQuery] = useState("");
  const [letter, setLetter] = useState(null);

  const available = useMemo(
    () => new Set(GLOSSARY.map((g) => norm(g.term)[0].toUpperCase())),
    [],
  );

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    return GLOSSARY.filter((g) => {
      if (letter && norm(g.term)[0].toUpperCase() !== letter) return false;
      if (q && !norm(g.term).includes(q) && !norm(g.def).includes(q)) return false;
      return true;
    }).sort((a, b) => norm(a.term).localeCompare(norm(b.term)));
  }, [query, letter]);

  return (
    <>
      <Seo
        title="Glossaire des paris sportifs : tous les termes expliqués"
        description="Mise, cote, rollover, freebet, cash-out, KYC... Le glossaire A-Z des paris sportifs pour débutants."
        path="/comprendre/glossaire"
      />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="max-w-[760px]">
          <Breadcrumbs
            items={[{ label: "Comprendre les paris" }, { label: "Glossaire des paris sportifs" }]}
          />
          <Reveal>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#0B1F3A] sm:text-4xl">
              Glossaire des paris sportifs
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              Tous les termes techniques expliqués simplement, de A à Z.
            </p>
          </Reveal>

          <div className="relative mt-8">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <input
              type="search"
              data-testid="glossary-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un terme (ex. rollover, cote, freebet...)"
              aria-label="Rechercher dans le glossaire"
              className="w-full rounded-full border border-slate-300 bg-white py-3 pl-12 pr-4 text-base shadow-sm transition-colors focus:border-[#2BB08A]"
            />
          </div>

          <div className="mt-5 flex flex-wrap gap-1.5" role="group" aria-label="Filtrer par lettre">
            <button
              type="button"
              data-testid="glossary-letter-all"
              onClick={() => setLetter(null)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                letter === null
                  ? "bg-[#0B1F3A] text-white"
                  : "bg-[#F3F5F8] text-slate-600 hover:bg-slate-200"
              }`}
            >
              Tous
            </button>
            {LETTERS.map((l) => {
              const has = available.has(l);
              return (
                <button
                  key={l}
                  type="button"
                  data-testid={`glossary-letter-${l}`}
                  disabled={!has}
                  onClick={() => setLetter(letter === l ? null : l)}
                  className={`h-8 w-8 rounded-full text-xs font-semibold transition-colors ${
                    letter === l
                      ? "bg-[#0B1F3A] text-white"
                      : has
                        ? "bg-[#F3F5F8] text-slate-600 hover:bg-slate-200"
                        : "cursor-not-allowed bg-transparent text-slate-300"
                  }`}
                >
                  {l}
                </button>
              );
            })}
          </div>

          <p className="mt-4 text-sm text-slate-500" aria-live="polite" data-testid="glossary-count">
            {filtered.length} terme{filtered.length > 1 ? "s" : ""}
          </p>

          <dl className="mt-6 space-y-3" data-testid="glossary-list">
            {filtered.map((g) => (
              <div
                key={g.term}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <dt className="font-display text-base font-semibold text-[#0B1F3A]">
                  <span className="mr-2 text-[#2BB08A]">{norm(g.term)[0].toUpperCase()}</span>
                  {g.term}
                </dt>
                <dd className="mt-1.5 text-[15px] leading-relaxed text-slate-600">{g.def}</dd>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="rounded-xl bg-[#F3F5F8] p-6 text-center text-slate-500">
                Aucun terme ne correspond à votre recherche.
              </p>
            )}
          </dl>
        </div>
      </div>
    </>
  );
}
